/**
 * Local-date utilities. The whole app uses YYYY-MM-DD strings as the date
 * key for meals + weight. We MUST compute these from the user's local time
 * — not from new Date().toISOString() which returns UTC and silently
 * "deletes" the user's evening entries the moment UTC ticks past midnight
 * (see bug-bash M11).
 *
 * Trade-off: the date is taken from the device's clock + Intl timezone.
 * Cross-device consistency therefore depends on the device knowing its
 * own zone (basically always true on phones / browsers). If we ever add
 * a server-side timezone preference (per-user) we'd compute from that
 * instead. Today the device is the source of truth.
 */

/** YYYY-MM-DD in the user's local timezone. */
export function localToday(): string {
  return localDate(new Date())
}

/** YYYY-MM-DD for yesterday in the user's local timezone. */
export function localYesterday(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return localDate(d)
}

/** YYYY-MM-DD in the user's local timezone, for any Date. */
export function localDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/**
 * "today" / "yesterday" / "Sun, May 24" for a YYYY-MM-DD string.
 * Parses as a local-zone date (not UTC) so we don't show "May 23" for
 * "2026-05-24" when the device is west of GMT.
 */
export function formatDateRelative(ymd: string): string {
  if (ymd === localToday()) return "today"
  if (ymd === localYesterday()) return "yesterday"
  const [y, m, d] = ymd.split("-").map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}
